import { Checkbox, Divider, FormControlLabel, Grid, InputAdornment, Paper, TextField, Typography } from "@mui/material";
import type { Dayjs } from "dayjs";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import type { UserRole } from "../../auth/auth";
import CalculationVersion from "../../calculations/CalculationVersion";
import DatePicker from "../../components/datePicker/DatePickerV2";
import type { CustomerListItem } from "../../customers/customersApi";
import { CustomerSection } from "./CustomerSection";
import { PackageInfo } from "./PackageInfo";

export interface CalculationBasicData {
  name: string;
  customer?: CustomerListItem;
  isPackageVersion?: boolean;
  margin: number;
  startDate: Dayjs | null;
  freePeopleCount: number;
  peopleCount: number;
  additionalInfo: string;
}

interface AdministrationSectionProps {
  readonly initialValue: CalculationBasicData;
  readonly onChange: (update: Partial<CalculationBasicData>) => void;
  readonly isReadonly: boolean;
}

export const AdministrationSection: FC<AdministrationSectionProps> = ({ initialValue, isReadonly, onChange }) => {
  const handleCustomerChange = useCallback(
    (customer: CustomerListItem | null) => onChange({ customer: customer ?? undefined }),
    [onChange],
  );
  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 3,
        }}
      >
        Pola administracyjne
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 5 }}>
          <CustomerSection
            defaultValue={initialValue.customer ?? null}
            onChange={handleCustomerChange}
            disabled={false}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Marża"
            type="number"
            variant="outlined"
            fullWidth
            disabled={isReadonly}
            onFocus={(e) => e.target.select()}
            defaultValue={initialValue.margin ?? ""}
            onChange={(e) => onChange({ margin: Number(e.target.value) })}
            slotProps={{
              input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
              htmlInput: { min: 0 },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }} sx={{ display: "flex", alignItems: "center" }}>
          <FormControlLabel
            control={
              <Checkbox
                defaultChecked={initialValue.isPackageVersion}
                onChange={(e) => onChange({ isPackageVersion: e.target.checked })}
                color="primary"
                disabled={isReadonly}
              />
            }
            label="Wersja pakietowa"
          />
        </Grid>
      </Grid>
      <Divider sx={{ mb: 4 }} />
    </>
  );
};

interface Props {
  readonly onApplyStartDate: (startDate: Dayjs | null) => void;
  readonly initialValue: CalculationBasicData;
  readonly onChange: (update: Partial<CalculationBasicData>) => void;
  readonly onAutoFill: () => void;
  readonly versions: { versionName?: string; metadata?: { createdAt: string; createdBy?: string } }[];
  readonly numberOfSavedVersions?: number;
  readonly selectedVersionIdx: number;
  readonly onVersionChange: (idx: number) => void;
  readonly onVersionNameChange: (newName: string | null) => void;
  readonly role: UserRole | undefined;
  readonly isReadonly: boolean;
  readonly draftVersionName: string | undefined;
  readonly packageId: string | null;
}

export const BasicData: FC<Props> = ({
  initialValue,
  packageId,
  isReadonly,
  role,
  numberOfSavedVersions,
  versions,
  selectedVersionIdx,
  draftVersionName,
  onChange,
  onApplyStartDate,
  onAutoFill,
  onVersionNameChange,
  onVersionChange,
}) => {
  const [participants, setParticipants] = useState({
    peopleCount: initialValue.peopleCount,
    freePeopleCount: initialValue.freePeopleCount,
  });
  useEffect(() => {
    setParticipants({
      peopleCount: initialValue.peopleCount,
      freePeopleCount: initialValue.freePeopleCount,
    });
  }, [initialValue.peopleCount, initialValue.freePeopleCount]);

  const [additionalInfo, setAdditionalInfo] = useState(initialValue.additionalInfo ?? "");
  const additionalInfoRef = useRef(additionalInfo);
  useEffect(() => {
    setAdditionalInfo(initialValue.additionalInfo ?? "");
    additionalInfoRef.current = initialValue.additionalInfo ?? "";
  }, [initialValue.additionalInfo]);

  const isCustomer = role === "CUSTOMER";

  return (
    <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 1, border: "1px solid rgba(0,0,0,0.08)" }}>
      {!isCustomer && <AdministrationSection initialValue={initialValue} isReadonly={isReadonly} onChange={onChange} />}
      <Typography
        variant="h6"
        sx={{
          fontWeight: "bold",
          mb: 3,
        }}
      >
        Parametry Główne
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Nazwa własna kalkulacji"
            variant="outlined"
            required
            fullWidth
            disabled={isReadonly || !!numberOfSavedVersions}
            defaultValue={initialValue.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <CalculationVersion
            versions={versions}
            numberOfSavedVersions={numberOfSavedVersions ?? versions.length}
            onVersionChange={onVersionChange}
            selectedIdx={selectedVersionIdx}
            onVersionNameChange={onVersionNameChange}
            draftVersionName={draftVersionName}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <DatePicker
            label="Data rozpoczęcia"
            data-testId="start-date-edit"
            fullWidth
            value={initialValue.startDate}
            onChange={(e) => {
              onApplyStartDate(e);
            }}
            disabled={isReadonly}
          />

          {!isReadonly && (
            <Typography
              variant="caption"
              color="primary"
              sx={{
                mt: 0.5,
                display: "inline-block",
                cursor: "pointer",
                fontWeight: "bold",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={() => onApplyStartDate(null)}
            >
              Zastosuj w kalkulacji
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField
            label="Przewidywana liczba osób"
            type="number"
            variant="outlined"
            fullWidth
            disabled={isReadonly}
            onFocus={(e) => e.target.select()}
            value={participants.peopleCount ?? ""}
            onChange={(e) => {
              const peopleCount = Number(e.target.value);
              setParticipants((prev) => ({ ...prev, peopleCount }));
              onChange({ peopleCount });
            }}
            slotProps={{
              htmlInput: { min: 0 },
            }}
          />
          {!isReadonly && (
            <Typography
              variant="caption"
              color="primary"
              sx={{
                mt: 0.5,
                display: "inline-block",
                cursor: "pointer",
                fontWeight: "bold",
                "&:hover": { textDecoration: "underline" },
              }}
              onClick={onAutoFill}
            >
              Automatycznie uzupełnij kalkulację ({participants.peopleCount + participants.freePeopleCount} os.)
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField
            label="Osoby obsługi"
            type="number"
            variant="outlined"
            fullWidth
            disabled={isReadonly}
            onFocus={(e) => e.target.select()}
            defaultValue={participants.freePeopleCount}
            onChange={(e) => {
              const freePeopleCount = Number(e.target.value);
              setParticipants((prev) => ({ ...prev, freePeopleCount }));
              onChange({ freePeopleCount });
            }}
            slotProps={{
              htmlInput: { min: 0 },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            label="Dodatkowe informacje / Notatki"
            multiline
            rows={2}
            variant="outlined"
            fullWidth
            disabled={isReadonly}
            value={additionalInfo}
            onChange={(e) => {
              const value = e.target.value;
              additionalInfoRef.current = value;
              setAdditionalInfo(value);
            }}
            onBlur={() => {
              onChange({
                additionalInfo: additionalInfoRef.current,
              });
            }}
          />
        </Grid>
      </Grid>
      {isReadonly && packageId && <PackageInfo packageId={packageId} />}
    </Paper>
  );
};
